import { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, ButtonGroup, Col, Container, FloatingLabel, Form, FormControl, FormSelect, InputGroup, Row, Spinner, ToggleButton } from 'react-bootstrap'
import logoRaudo from '../assets/images/logo-raudo.png'
import logoRaudoLight from '../assets/images/logo-raudo-light.png'
const API_BASE = process.env.REACT_APP_API_URL || ''
const DEFAULT_PUBLIC_CALENDAR_CONFIG = {
  closedHolidays: true,
  closedSaturday: true,
  closedSunday: true
}

const toDateInput = (d) => {
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const getEasterSunday = (year) => {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const g = Math.floor((8 * b + 13) / 25)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(year, month - 1, day)
}

const isCzechHoliday = (date) => {
  const year = date.getFullYear()
  const fixed = new Set([
    `${year}-01-01`,
    `${year}-05-01`,
    `${year}-05-08`,
    `${year}-07-05`,
    `${year}-07-06`,
    `${year}-09-28`,
    `${year}-10-28`,
    `${year}-11-17`,
    `${year}-12-24`,
    `${year}-12-25`,
    `${year}-12-26`
  ])
  const easterSunday = getEasterSunday(year)
  const goodFriday = new Date(easterSunday)
  goodFriday.setDate(goodFriday.getDate() - 2)
  const easterMonday = new Date(easterSunday)
  easterMonday.setDate(easterMonday.getDate() + 1)
  fixed.add(toDateInput(goodFriday))
  fixed.add(toDateInput(easterMonday))
  return fixed.has(toDateInput(date))
}

const isDateBlockedByConfig = (isoDate, config) => {
  if (!isoDate) return false
  const d = new Date(`${isoDate}T00:00:00`)
  const day = d.getDay()
  if (config?.closedSunday && day === 0) return true
  if (config?.closedSaturday && day === 6) return true
  if (config?.closedHolidays && isCzechHoliday(d)) return true
  return false
}

const PublicReservation = () => {
  const minDate = useMemo(() => toDateInput(new Date()), [])
  const maxDate = useMemo(() => {
    const d = new Date()
    d.setMonth(d.getMonth() + 2)
    return toDateInput(d)
  }, [])

  const [slots, setSlots] = useState([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [sendingCode, setSendingCode] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [validatedOrder, setValidatedOrder] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [smsError, setSmsError] = useState('')
  const [smsSuccess, setSmsSuccess] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [calendarConfig, setCalendarConfig] = useState(DEFAULT_PUBLIC_CALENDAR_CONFIG)

  const getInitialForm = () => ({
    datum: minDate,
    terminStart: '',
    servisniUkon: '1',
    jmeno: '',
    email: '',
    predvolba: '+420',
    telefon: '',
    spz: '',
    poznamka: '',
    souhlas: false,
    kod: ''
  })

  const [form, setForm] = useState(getInitialForm)

  const isBlockedDate = isDateBlockedByConfig(form.datum, calendarConfig)
  const isPhoneInvalid = validatedOrder && !/^\d{9}$/.test(String(form.telefon || '').trim())

  useEffect(() => {
    const loadCalendarConfig = async () => {
      try {
        const res = await fetch(`${API_BASE}/rezervace/public/config`)
        const data = await res.json()
        if (!res.ok) return
        setCalendarConfig((prev) => ({ ...prev, ...data }))
      } catch {
        // fallback na default
      }
    }
    loadCalendarConfig()
  }, [])

  useEffect(() => {
    if (!form.datum) return
    if (isDateBlockedByConfig(form.datum, calendarConfig)) {
      setSlots([])
      setForm((prev) => ({ ...prev, terminStart: '' }))
      setError('')
      return
    }
    const loadSlots = async () => {
      setLoadingSlots(true)
      setError('')
      try {
        const url = `${API_BASE}/rezervace/public/slots?date=${encodeURIComponent(form.datum)}`
        const res = await fetch(url)
        const data = await res.json()
        setSlots(Array.isArray(data?.slots) ? data.slots : [])
      } catch (e) {
        setError('Nepodařilo se načíst dostupné termíny.')
        setSlots([])
      } finally {
        setLoadingSlots(false)
      }
    }
    loadSlots()
  }, [form.datum, calendarConfig])

  const onChange = (e) => {
    const { name, value, type, checked } = e.target
    if (name === 'datum') {
      if (isDateBlockedByConfig(value, calendarConfig)) {
        setError('')
        setSlots([])
        setForm((prev) => ({ ...prev, datum: value, terminStart: '' }))
        return
      }
      setError('')
      setForm((prev) => ({ ...prev, datum: value, terminStart: '' }))
      return
    }
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const formatSelectedTerm = (value) => {
    if (!value) return ''
    const d = new Date(String(value).replace(' ', 'T'))
    if (Number.isNaN(d.getTime())) return ''
    const weekday = new Intl.DateTimeFormat('cs-CZ', { weekday: 'long' }).format(d)
    const day = d.getDate()
    const month = d.getMonth() + 1
    const year = d.getFullYear()
    const time = new Intl.DateTimeFormat('cs-CZ', { hour: 'numeric', minute: '2-digit' }).format(d)
    return `${weekday} ${day}.${month}.${year} ${time}`
  }

  const sendCode = async () => {
    setSendingCode(true)
    setSmsError('')
    setSmsSuccess('')
    try {
      const fullPhone = `${form.predvolba}${String(form.telefon || '').replace(/\D/g, '')}`
      const res = await fetch(`${API_BASE}/rezervace/public/sms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telefon: fullPhone })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.message || 'Nepodařilo se odeslat SMS.')
      setSmsSuccess('Autorizační SMS kód byl odeslán.')
    } catch (e) {
      setSmsError(e.message || 'Nepodařilo se odeslat SMS.')
    } finally {
      setSendingCode(false)
    }
  }

  const submit = async (e) => {
    e.preventDefault()
    const formEl = e.currentTarget
    const isValid = formEl.checkValidity()
    setValidatedOrder(true)
    if (!isValid) {
      setError('Doplňte prosím vstupní údaje.')
      return
    }
    setError('')
    setSuccess('')
    setSubmitting(true)
    try {
      const fullPhone = `${form.predvolba}${String(form.telefon || '').replace(/\D/g, '')}`
      const payload = {
        ...form,
        telefon: fullPhone,
        spz: String(form.spz || '').replace(/\s+/g, '').toUpperCase()
      }
      const res = await fetch(`${API_BASE}/rezervace/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.message || 'Nepodařilo se vytvořit rezervaci.')
      const terminText = formatSelectedTerm(data?.terminStart || data?.start || form.terminStart)
      setSuccess(`Vaše rezervace na ${terminText} byla úspěšně vytvořena. Potvrzení bylo odesláno na uvedený email. Děkujeme.`)
      setForm(getInitialForm())
      setSlots([])
      setValidatedOrder(false)
      setSubmitted(true)
    } catch (e2) {
      setError(e2.message || 'Nepodařilo se vytvořit rezervaci. Kontaktujte nás, prosím, telefonicky na čísle 721 291 419. Děkujeme za pochopení.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Container fluid className="public-reservation-shell p-0">
      <Row className="g-0 min-vh-100 public-reservation">
        <Col md={6} xs={12} className="reservation-form-pane">
          <div
            className="reservation-form-watermark"
            aria-hidden="true"
          />
          <div className="reservation-form-wrap">
            <div className="reservation-form-content">
              <div className="reservation-intro mb-3">
                <div className="reservation-logo-wrap mt-4 mb-3">
                  <img src={logoRaudo} alt="Logo" className="reservation-logo-default" height="28" />
                  <img src={logoRaudoLight} alt="Logo" className="reservation-logo-light" height="28" />
                </div>
                <h2 className="reservation-main-title text-center mb-0 pt-3">REZERVACE PNEUSERVISU</h2>
                <p className="text-center pb-3">Objednejte se online na přezutí pneumatik, opravu defektu nebo převážení kol</p>
              </div>
              {success && (
                <Alert className='d-flex align-items-center mt-2 mb-2' variant="success">
                  <i className="bi bi-check-lg me-2"></i>
                  {success}
                </Alert>
              )}
              
              {submitted ? (
                <div className="mt-3 text-center">
                  <Button className='p-3' type="button" variant="primary" onClick={() => window.location.reload()}>
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Zpět na rezervace
                  </Button>
                </div>
              ) : (
              <Form noValidate validated={validatedOrder} onSubmit={submit}>
                <div className="reservation-section mb-3">
                  <div className="reservation-section-heading mt-4">
                    <i className="bi bi-calendar4-event reservation-section-icon"></i>
                    <div>
                      <div className="reservation-section-title">Termín rezervace</div>
                      <div className="reservation-section-subtitle">Vyberte si volný termín návštěvy</div>
                    </div>
                  </div>
                  <Row className="g-2 mb-2">
                    <Col md={4}>
                      <FloatingLabel label="kalendář *">
                        <Form.Control
                          type="date"
                          name="datum"
                          min={minDate}
                          max={maxDate}
                          value={form.datum}
                          onChange={onChange}
                          required
                        />
                      </FloatingLabel>
                    </Col>
                    <Col md={8}>
                      <FloatingLabel label="vybraný termín *">
                        <Form.Control
                          value={formatSelectedTerm(form.terminStart)}
                          readOnly
                          required
                          className="reservation-selected-term"
                        />
                      </FloatingLabel>
                    </Col>
                  </Row>

                  <Form.Label className="d-block small mb-2">Dostupné termíny</Form.Label>
                  {isBlockedDate ? (
                    <Alert variant="warning" className="mb-0">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Na tento den nelze vytvořit rezervaci.
                    </Alert>
                  ) : loadingSlots ? (
                    <div className="d-flex align-items-center gap-2 text-muted small py-2">
                      <Spinner size="sm" />
                      Načítám dostupné termíny...
                    </div>
                  ) : (
                    <div className="slot-grid">
                      {slots.map((slot) => {
                        const time = String(slot.start).slice(11, 16)
                        const busy = slot.status === 'busy'
                        const unavailable = slot.status === 'unavailable'
                        const active = form.terminStart === slot.start
                        return (
                          <Button
                            key={slot.start}
                            type="button"
                            size="sm"
                            variant={busy ? 'danger' : (unavailable ? 'outline-secondary' : (active ? 'primary' : 'outline-success'))}
                            className="slot-pill"
                            disabled={busy || unavailable}
                            onClick={() => setForm((prev) => ({ ...prev, terminStart: slot.start }))}
                          >
                            {time}
                          </Button>
                        )
                      })}
                    </div>
                  )}

                  <Form.Label className="d-block small mt-3 mb-2">Servisní úkon</Form.Label>
                  <ButtonGroup className="reservation-service-options">
                    {[
                      { value: '1', label: 'Přezutí kol', icon: 'bi-arrow-left-right' },
                      { value: '2', label: 'Oprava defektu', icon: 'bi-wrench' },
                      { value: '3', label: 'Převážení kol', icon: 'bi-disc' }
                    ].map((option) => (
                      <ToggleButton
                        key={option.value}
                        id={`servisni-ukon-${option.value}`}
                        type="radio"
                        name="servisniUkon"
                        value={option.value}
                        checked={form.servisniUkon === option.value}
                        onChange={onChange}
                        variant="outline-secondary"
                        required
                      >
                        <i className={`bi ${option.icon} me-2`}></i>
                        {option.label}
                      </ToggleButton>
                    ))}
                  </ButtonGroup>
                </div>

                <div className="reservation-section mb-3">
                  <div className="reservation-section-heading">
                    <i className="bi bi-person reservation-section-icon"></i>
                    <div>
                      <div className="reservation-section-title">Kontaktní údaje</div>
                      <div className="reservation-section-subtitle">Vyplňte prosím všechny povinné údaje</div>
                    </div>
                  </div>
                  <Row className="g-2">
                    <Col md={6}>
                      <FloatingLabel label="jméno nebo název firmy *">
                        <Form.Control name="jmeno" value={form.jmeno} onChange={onChange} required />
                      </FloatingLabel>
                    </Col>
                    <Col md={6}>
                      <FloatingLabel label="email">
                        <Form.Control type="email" name="email" value={form.email} onChange={onChange} />
                      </FloatingLabel>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formTelefon">
                        <InputGroup>
                          <FormSelect
                            className="reservation-phone-prefix"
                            name="predvolba"
                            value={form.predvolba}
                            onChange={onChange}
                            isInvalid={isPhoneInvalid}
                            style={{ maxWidth: '150px' }}
                          >
                            <option value="+420">CZ +420</option>
                            <option value="+421">SK +421</option>
                            <option value="+48">PL +48</option>
                            <option value="+49">DE +49</option>
                            <option value="+43">AT +43</option>
                          </FormSelect>
                          <FloatingLabel label="telefon *">
                            <FormControl
                              type="tel"
                              name="telefon"
                              value={form.telefon}
                              onChange={onChange}
                              pattern="^\d{9}$"
                              title="Zadejte 9 číslic bez mezer"
                              required
                            />
                          </FloatingLabel>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <FloatingLabel label="spz *">
                        <Form.Control
                          name="spz"
                          value={form.spz}
                          onChange={onChange}
                          onBlur={(e) => setForm((prev) => ({ ...prev, spz: String(e.target.value || '').replace(/\s+/g, '').toUpperCase() }))}
                          required
                        />
                      </FloatingLabel>
                    </Col>
                    <Col md={12}>
                      <FloatingLabel label="poznámka">
                        <Form.Control as="textarea" style={{ height: '100px' }} name="poznamka" value={form.poznamka} onChange={onChange} />
                      </FloatingLabel>
                    </Col>
                  </Row>
                </div>

                <div className="reservation-section mb-3">
                  <div className="reservation-section-heading">
                    <i className="bi bi-shield-lock reservation-section-icon"></i>
                    <div>
                      <div className="reservation-section-title">Ověření telefonního čísla</div>
                      <div className="reservation-section-subtitle">Proveďte ověření pomocí SMS zprávy</div>
                    </div>
                  </div>
                  <Row className="g-2">
                    <Col md={8}>
                      <FloatingLabel label="autorizační SMS kód *">
                        <Form.Control name="kod" value={form.kod} onChange={onChange} required />
                      </FloatingLabel>
                    </Col>
                    <Col md={4} className="d-flex align-items-end">
                      <Button type="button" className="w-100 p-3" variant="outline-primary" onClick={sendCode} disabled={sendingCode || !/^\d{9}$/.test(String(form.telefon || '').trim())}>
                        <i className="bi bi-chat-dots me-2"></i>
                        {sendingCode ? 'Odesílám...' : 'Zaslat SMS kód'}
                      </Button>
                    </Col>
                  </Row>
                  {smsError && (
                    <Alert className='d-flex align-items-center mt-2 mb-0' variant="danger">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {smsError}
                    </Alert>
                  )}
                  {smsSuccess && (
                    <Alert className='d-flex align-items-center mt-2 mb-0' variant="success">
                      <i className="bi bi-check-lg me-2"></i>
                      {smsSuccess}
                    </Alert>
                  )}
                </div>

                <div className="mt-3 p-3 rounded border bg-light-subtle">
                  <div className="small mb-2"><Badge bg="secondary">Rezervační podmínky</Badge></div>
                  <div className="small">Vyplněním a odesláním rezervace se závazně přihlašujete k vybranému termínu. Na jedno telefonní číslo je možné rezervovat maximálně 5 vozidel. Zásady ochrany osobních údajů naleznete <a href="https://www.raudo.cz/gdpr" title='Ochrana osobních údajů' target='blank'>zde</a>.</div>
                </div>

                <Form.Check
                  className="mt-3 small"
                  type="checkbox"
                  name="souhlas"
                  checked={form.souhlas}
                  onChange={onChange}
                  required
                  label="Souhlasím se zpracováním osobních údajů *"
                />
                {error && (
                <Alert className='d-flex align-items-center mt-2 mb-2' variant="danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </Alert>
              )}
                <div className="d-flex justify-content-between align-items-end mt-3 mb-4">
                  <div className=''>
                    <p className='small mb-0'>
                      <span className='fw-semibold'>RAUDO - výrobní družstvo invalidů</span><br/>
                      Uničovská 370/19, 787 01 Šumperk
                    </p>
                  </div>
                  <div className="d-flex gap-1">
                    <Button className="p-3" href="https://www.raudo.cz" variant="secondary">
                      <i className="bi bi-arrow-left me-2"></i>
                      Zpět
                    </Button>
                    <Button className='p-3' type="submit" variant="primary" disabled={submitting || !form.terminStart}>
                      <i className="bi bi-save me-2"></i>
                      {submitting ? 'Ukládám...' : 'Odeslat rezervaci'}
                    </Button>
                  </div>
                </div>
              </Form>
              )}
            </div>
          </div>
        </Col>
        <Col md={6} className="reservation-side-pane d-none d-md-flex" />
      </Row>
    </Container>
  )
}

export default PublicReservation
